import { Header, notify, StatsCard } from "@general-backoffice/core";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Col, Container, Row } from "reactstrap";

import { referralCodesEndpoints, referrerEndpoints } from "../../../../api";
import useReferrals from "../../../../components/referral/hooks/useReferrals";
import PartnerReferralsTableCard from "../../../../components/referral/PartnerReferralsTableCard";
import useReferralCode from "../../../../components/referrer/hooks/useReferralCode";
import CreateReferralCodeModal from "../../../../components/referrer/partner/CreateReferralCodeModal";
import ReferralCodesTableCard from "../../../../components/referrer/partner/ReferralCodesTableCard";
import TnCModal from "../../../../components/referrer/partner/TnCModal";
import ReferralLinkCard from "../../../../components/referrer/ReferralLinkCard";
import generateReferrerLink from "../../../../components/referrer/utils/generateReferrerLink";
import useFetch from "../../../../hooks/useFetch";
import type { RootState } from "../../../../redux/reducers/session/state";
import refreshMe from "../../../../utils/refreshMe";

const Referrals = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("dashboard", { keyPrefix: "referrerPartner" });
  const { t: tDistributor } = useTranslation("dashboard", { keyPrefix: "distributorClients" });
  const state = useSelector((state: RootState) => state);
  const dispatch = useDispatch();

  const tncAccepted = state?.sessionState?.user?.hasAcceptedAffiliateTnC;

  const {
    referrals: allReferrals = [],
    isLoading: isLoadingReferrals,
    ...referralsProps
  } = useReferrals({
    pagination: { pageSize: 100 },
    mine: true,
  });

  // Filter referrals on the UI
  // Active: not expired, not claimed, referee is active
  // Pending: not expired, not claimed, referee is NOT active (hasn't activated bot yet)
  const activeReferrals = useMemo(
    () => allReferrals.filter((r) => !r.expired && !r.hasClaimedReward && r.referee?.isActive),
    [allReferrals]
  );
  const pendingReferrals = useMemo(
    () => allReferrals.filter((r) => !r.expired && !r.hasClaimedReward && !r.referee?.isActive),
    [allReferrals]
  );
  const totalActiveReferrals = activeReferrals.length;
  const totalPendingReferrals = pendingReferrals.length;

  const { state: referrerCode, isLoading: isLoadingReferrerCode } = useFetch(
    () => referrerEndpoints.getReferrerCode().then(({ data }) => data),
    { auto: tncAccepted }
  );
  const referrerLink = referrerCode ? generateReferrerLink(referrerCode) : undefined;

  const {
    state: referralCodesResponse,
    isLoading: isLoadingReferralCodes,
    refresh: requestReferralCodes,
  } = useFetch(() => referralCodesEndpoints.getMyReferralCodes().then(({ data }) => data));
  const referralCodes = referralCodesResponse ?? [];

  // For referral code creation modal
  const {
    referralCode: referralCodeToCreate,
    setReferralCode: setReferralCodeToCreate,
    isLoading: isLoadingReferralCodeToCreate,
    update: createReferralCodeToCreate,
  } = useReferralCode();

  const isOpenTnCModal = !tncAccepted;

  const convertionRate = useMemo(() => {
    if (!totalActiveReferrals || !totalPendingReferrals) return 0;
    return (totalActiveReferrals / (totalActiveReferrals + totalPendingReferrals)) * 100;
  }, [totalActiveReferrals, totalPendingReferrals]);

  return (
    <React.Fragment>
      <TnCModal
        isOpen={isOpenTnCModal}
        close={() => navigate("/")}
        tnCText={"tncDistributor"}
        acceptTerms={() => {
          referrerEndpoints
            .acceptTnC()
            .then(() => refreshMe({ state, dispatch }))
            .catch(() => notify.error(t("errorOnAcceptTnC")));
        }}
      />

      <CreateReferralCodeModal
        referralCode={referralCodeToCreate}
        close={() => setReferralCodeToCreate(null)}
        setReferralCode={setReferralCodeToCreate}
        isLoading={isLoadingReferralCodeToCreate}
        create={() => {
          createReferralCodeToCreate().then(() => {
            requestReferralCodes();
            setReferralCodeToCreate(null);
          });
        }}
      />

      <Header.BreadCrumb
        color="secondary"
        isDark={false}
        buttons={
          [
            // {
            //   children: <i className="fas fa-info-circle"></i>,
            //   color: "default",
            //   size: "md",
            //   className: "shadow-none position-absolute",
            //   style: { transform: "translateY(-50%)" },
            //   onClick: () => setIsOpenInfoModal(true)
            // }
          ]
        }
      >
        <Col xs={12} xl={6}>
          <Row>
            <Col xs={12} lg={4} xl={12}>
              <StatsCard.Number
                color="default"
                isDark={true}
                number={totalActiveReferrals}
                suffix={tDistributor("activeClients.suffix")}
                title={tDistributor("activeClients.title")}
                icon="fas fa-users"
                description={tDistributor("activeClients.description")}
              />
            </Col>
            {/* <Col xs={12} md={6} lg={4} xl={12}>
              <StatsCard.Number
                color="danger"
                isDark={true}
                number={0}
                suffix="Clientes"
                title="Total clientes inactivos"
                icon="fas fa-user-tag"
                description="Número de clientes referidos con unicamente bots pausados"
              />
            </Col> */}
            <Col xs={12} md={6} lg={4} xl={12}>
              <StatsCard.Number
                color="default"
                isDark={false}
                number={totalPendingReferrals}
                suffix={tDistributor("pendingClients.suffix")}
                title={tDistributor("pendingClients.title")}
                icon="fas fa-user-tag"
                description={tDistributor("pendingClients.description")}
              />
            </Col>
            <Col xs={12} md={6} lg={4} xl={12}>
              {/* TODO: Fix type - StatsCard.Number expects number but toFixed returns string */}
              <StatsCard.Number
                color="success"
                number={parseFloat(convertionRate.toFixed(2))}
                suffix={tDistributor("conversionRate.suffix")}
                title={tDistributor("conversionRate.title")}
                icon="fas fa-percentage text-success"
                description={tDistributor("conversionRate.description")}
                isDark
              />
            </Col>
          </Row>
        </Col>
        <Col xs={12} xl={6}>
          <Row>
            <Col xs={12} lg={6} xl={12}>
              <ReferralLinkCard
                link={referrerLink}
                isLoading={isLoadingReferrerCode}
                title={t("referralLinkCard.title")}
                subtitle={t("referralLinkCard.subtitle")}
                onClickButton={async () => {
                  // TODO: Fix type - referrerLink should always be string when button is clickable
                  await navigator.clipboard.writeText(referrerLink ?? "");
                  notify(t("referralLinkCard.messageLinkCopied"));
                }}
              />
            </Col>
            <Col xs={12} lg={6} xl={12}>
              <ReferralCodesTableCard
                setReferralCodeToCreate={setReferralCodeToCreate}
                referralCodes={referralCodes}
                isLoading={isLoadingReferralCodes}
              />
            </Col>
          </Row>
        </Col>
      </Header.BreadCrumb>
      <Container className="mt--6" fluid>
        <Row>
          <Col xs={12} xl={8}>
            <PartnerReferralsTableCard
              {...referralsProps}
              totalReferrals={totalActiveReferrals}
              isLoading={isLoadingReferrals}
              hideButtons
              referrals={activeReferrals}
              onUserClick={(r) => navigate(`/users/${r.referee?.id}`)}
            />
          </Col>
          <Col xs={12} xl={4}>
            <PartnerReferralsTableCard
              {...referralsProps}
              totalReferrals={totalPendingReferrals}
              isLoading={isLoadingReferrals}
              referrals={pendingReferrals}
              onUserClick={(r) => navigate(`/users/${r.referee?.id}`)}
              columnsToHide={["badges", "uncollectedCommission", "totalCommission"]}
              title={tDistributor("pendingTitle")}
              hideButtons
            />
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default Referrals;
